import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/standartComponents/header';
import { getPasswordResetInfo, resetPassword } from '../../lib/allauth';
import css from './passwordReset.module.css';

const APP_HOME = '/mylearning';
const REDIRECT_DELAY = 2500; // мс, затримка перед редіректом після успіху

const EyeOpen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
);
const EyeClosed = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
);

// loading  — перевіряємо key через GET
// invalid  — key зламаний/прострочений (400) або конфлікт (409)
// form     — key валідний, показуємо форму нового пароля
// success  — пароль змінено, користувач автентифікований, редірект в апку
const VerifyGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ErrorGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M7 7l10 10M17 7L7 17" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const PasswordReset = ({ authUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { key } = useParams();

  const [pageStatus, setPageStatus] = useState('loading');
  const [userInfo, setUserInfo] = useState(null);
  const [invalidMessage, setInvalidMessage] = useState(null);
  const checkedKey = useRef(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [isChangeLoading, setIsChangeLoading] = useState(false);

  useEffect(() => {
    if (!key) {
      setPageStatus('invalid');
      return;
    }
    if (checkedKey.current === key) return;
    checkedKey.current = key;

    (async () => {
      const result = await getPasswordResetInfo(key);
      if (result.status === 200) {
        setUserInfo(result.data?.data?.user || null);
        setPageStatus('form');
      } else {
        // 400: ключ зламаний/прострочений. 409: конфлікт (напр. вже залогінені іншим акаунтом).
        // Поки показуємо однаковий екран "посилання не працює" для обох,
        // але під низом виводимо реальне повідомлення від API — для дебагу.
        setInvalidMessage(result.data?.errors?.[0]?.message || null);
        setPageStatus('invalid');
      }
    })();
  }, [key]);

  useEffect(() => {
    if (pageStatus !== 'success') return undefined;
    const timer = setTimeout(() => navigate(APP_HOME, { replace: true }), REDIRECT_DELAY);
    return () => clearTimeout(timer);
  }, [pageStatus, navigate]);

  const submitNewPassword = async () => {
    setFormError(null);
    const errs = {};
    if (password.length < 8) errs.password = true;
    if (confirmPassword !== password) errs.confirmPassword = true;
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsChangeLoading(true);
    const result = await resetPassword(key, password);
    setIsChangeLoading(false);

    if (result.status === 200) {
      // пароль змінено, браузер автентифікований
      await window.refreshAuthUser?.();
      setPageStatus('success');
    } else if (result.status === 401) {
      // пароль змінено, але браузер не залогінений — ключ уже використано,
      // повторний сабміт тим самим key більше не спрацює (і не повинен)
      setPageStatus('changed');
    } else {
      // 400/409/інше — реальна помилка, ключ досі можна спробувати ще раз
      setFormError(
        result.data?.errors?.[0]?.message || t('Something went wrong, please try again')
      );
    }
  };

  return (
    <>
      <Header authUser={authUser} />
      <main className={css.reset}>
        <section className={css.reset__card}>
          {pageStatus === 'loading' && (
            <>
              <div className={`${css.reset__icon} ${css['reset__icon--loading']}`} aria-hidden="true">
                <span className={css.reset__spinner} />
              </div>
              <div className={css.reset__body} role="status" aria-live="polite">
                <h1 className={css.reset__title}>{t('Checking your link…')}</h1>
                <p className={css.reset__text}>{t('This will only take a moment')}</p>
              </div>
            </>
          )}

          {pageStatus === 'invalid' && (
            <>
              <div className={`${css.reset__icon} ${css['reset__icon--error']}`} aria-hidden="true">
                <span className={css.reset__glyph}><ErrorGlyph /></span>
              </div>
              <div className={css.reset__body}>
                <h1 className={css.reset__title}>{t("This link doesn't work")}</h1>
                <p className={css.reset__text}>
                  {t('The link is broken or has expired – request a new one and try again')}
                </p>
                {invalidMessage && (
                  <p className={css.reset__debug}>{invalidMessage}</p>
                )}
              </div>
              <div className={css.reset__actions}>
                <Link to="/signin" className={css.reset__btn}>{t('Sign in')}</Link>
                <Link to="/" className={`${css.reset__btn} ${css['reset__btn--secondary']}`}>
                  {t('Back to home')}
                </Link>
              </div>
            </>
          )}

          {pageStatus === 'form' && (
            <>
              <div className={css.reset__body} style={{ marginTop: 0 }}>
                <h1 className={css.reset__title}>{t('Set a new password')}</h1>
                <p className={css.reset__text}>{t('Choose a strong password for your account')}</p>
                {userInfo?.email && (
                  <p className={css.reset__email}>{userInfo.email}</p>
                )}
              </div>

              {formError && (
                <div className={css.reset__banner}>
                  <div className={css.reset__bannerIcon} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
                  </div>
                  <p className={css.reset__bannerText}>{formError}</p>
                </div>
              )}

              <div className={css.reset__form}>
                <div className={css.fg}>
                  <label className={css.fl}>{t('New password')}</label>
                  <div className={css.pwWrap}>
                    <input
                      className={`${css.fi} ${errors.password ? css.err : ''}`}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('Your password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      className={css.pwToggle}
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={t('Show password')}
                    >
                      {showPassword ? <EyeClosed /> : <EyeOpen />}
                    </button>
                  </div>
                  <div className={css.fiHint}>{t('Minimum 8 characters.')}</div>
                  <div className={`${css.fiErr} ${errors.password ? css.show : ''}`}>
                    {t('Password must contain at least 8 characters')}
                  </div>
                </div>

                <div className={css.fg}>
                  <label className={css.fl}>{t('Confirm password')}</label>
                  <input
                    className={`${css.fi} ${errors.confirmPassword ? css.err : ''}`}
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('Repeat your password')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <div className={`${css.fiErr} ${errors.confirmPassword ? css.show : ''}`}>
                    {t('Passwords do not match')}
                  </div>
                </div>

                <button
                  className={`${css.btnSubmit} ${isChangeLoading ? css.btnLoading : ''}`}
                  onClick={submitNewPassword}
                  disabled={isChangeLoading}
                >
                  {isChangeLoading && <span className={css.spinner} />}
                  <span>{isChangeLoading ? t('Saving…') : t('Save new password')}</span>
                </button>
              </div>
            </>
          )}

          {pageStatus === 'changed' && (
            <>
              <div className={`${css.reset__icon} ${css['reset__icon--done']}`} aria-hidden="true">
                <span className={css.reset__glyph}><VerifyGlyph /></span>
              </div>
              <div className={css.reset__body}>
                <h1 className={css.reset__title}>{t('Password changed')}</h1>
                <p className={css.reset__text}>{t('Sign in with your new password')}</p>
              </div>
              <div className={css.reset__actions}>
                <Link to="/signin" className={css.reset__btn}>{t('Sign in')}</Link>
              </div>
            </>
          )}

          {pageStatus === 'success' && (
            <>
              <div className={`${css.reset__icon} ${css['reset__icon--done']}`} aria-hidden="true">
                <span className={css.reset__glyph}><VerifyGlyph /></span>
              </div>
              <div className={css.reset__body} role="status" aria-live="polite">
                <h1 className={css.reset__title}>{t('Password changed')}</h1>
                <p className={css.reset__text}>{t("You're signed in, taking you to the app…")}</p>
              </div>
              <div className={css.reset__progress} aria-hidden="true">
                <span
                  className={css['reset__progress-bar']}
                  style={{ '--redirect-delay': `${REDIRECT_DELAY}ms` }}
                />
              </div>
              <div className={css.reset__actions}>
                <Link to={APP_HOME} replace className={css.reset__btn}>
                  {t('Continue to the app')}
                </Link>
              </div>
            </>
          )}
        </section>
      </main>
    </>
  );
};

export default PasswordReset;